/* eslint-disable max-lines */
import { useLazyQuery } from '@apollo/client';
import { doesUrlMatchNavigationParts, Navigation, NavigationItem } from '@deltasierra/features-left-navigation';
import { AppFrontendRoutes } from '@deltasierra/frontend-routing-app-frontend';
import { t } from '@deltasierra/shared';
import { isNotNullOrUndefined } from '@deltasierra/type-utilities';
import {
    Announcement01,
    BarChartSquare03,
    Beaker02,
    Bookmark,
    Brush03,
    CalendarDate,
    Compass03,
    FileCheck02,
    FileCheck03,
    FileShield03,
    Globe04,
    GraduationHat02,
    Hash01,
    Image03,
    Link04,
    Map02,
    MarkerPin01,
    MarkerPin06,
    Passport,
    PieChart03,
    Route,
    Send01,
    Ticket01,
    Users01,
} from '@untitled-ui/icons-react';
import * as React from 'react';
import { useAngularServiceContext } from '../../../common/componentUtils/angularServiceContexts';
import { useAuthRoles } from '../../../common/componentUtils/angularServiceHooks';
import { useDeprecatedAppFrontendRouter } from '../../../common/hooks/useAppFrontendRouter';
import { GET_LOCATION_FEATURES_QUERY } from '../../../common/locationFeatures';
import {
    GetLocationFeatures,
    GetLocationFeaturesVariables,
} from '../../../common/locationFeatures/__graphqlTypes/GetLocationFeatures';
import { LeftNavigationView } from '../enums';
import {
    GetShowGroupsOnDashboardForClient,
    GetShowGroupsOnDashboardForClientVariables,
} from '../queries/__graphqlTypes/GetShowGroupsOnDashboardForClient';
import { GET_SHOW_GROUPS_ON_DASHBOARD_FOR_CLIENT } from '../queries/GetShowGroupsOnDashboardForClient';
import { useUserBrandAccessCount } from './useUserBrandAccessCount';

type UseLeftNavigationMenuProps = {
    clientId: string | null;
    currentUrlPath: string;
    currentView: LeftNavigationView;
    locationId: string | null;
};

// eslint-disable-next-line max-lines-per-function, complexity
export function useLeftNavigationMenu({
    clientId,
    currentUrlPath,
    currentView,
    locationId,
}: UseLeftNavigationMenuProps): {
    homeHref: string;
    isLoading: boolean;
    navigation: Navigation<React.ReactNode>;
} {
    const appFrontendRouter = useDeprecatedAppFrontendRouter();
    const mvIdentity = useAngularServiceContext('mvIdentity');

    const [getFeatureFlagData, { data, loading }] = useLazyQuery<GetLocationFeatures, GetLocationFeaturesVariables>(
        GET_LOCATION_FEATURES_QUERY,
        {
            fetchPolicy: 'cache-and-network',
        },
    );

    const [getShowGroupsOnDashboardForClient, { data: showGroupsOnDashBoardData }] = useLazyQuery<
        GetShowGroupsOnDashboardForClient,
        GetShowGroupsOnDashboardForClientVariables
    >(GET_SHOW_GROUPS_ON_DASHBOARD_FOR_CLIENT, {
        fetchPolicy: 'cache-and-network',
    });

    React.useEffect(() => {
        if (clientId) {
            void getShowGroupsOnDashboardForClient({ variables: { id: clientId } });
        }
    }, [clientId, getShowGroupsOnDashboardForClient]);

    const { isAdmin, isManager } = useAuthRoles();

    React.useEffect(() => {
        if (clientId && locationId) {
            void getFeatureFlagData({
                variables: { locationId },
            });
        }
    }, [clientId, getFeatureFlagData, locationId]);

    const { loading: userBrandCountLoading, userBrandCount } = useUserBrandAccessCount(isAdmin || isManager);

    const canAccessMoreThanOneBrand = isNotNullOrUndefined(userBrandCount) && userBrandCount > 1;

    const isNewAssetLibraryEnabled: boolean = !loading && (data?.location?.features.enableNewAssetLibrary || false);
    const isNewGalleryPageEnabled: boolean = !loading && (data?.location?.features.enableNewGalleryPage || false);
    const isNewScheduledPageEnabled: boolean = !loading && (data?.location?.features.enableNewScheduledPage || false);
    const isNewConnectPageEnabled: boolean = !loading && (data?.location?.features.enableNewConnectPage || false);
    const isDashboardV2Enabled: boolean = !loading && (data?.location?.features.dashboardV2 || false);
    const isLocationDetailsV2Enabled: boolean = !loading && (data?.location?.features.enableLocationDetailsV2 || false);

    const itemVisibility = React.useMemo(() => {
        const canAccessEverything = mvIdentity.isAdmin() || mvIdentity.isManager();
        return {
            showAssets: mvIdentity.canAccessBuilder(),
            showCalendar: mvIdentity.canAccessPlanner(),
            showConnections: true,
            showLearn: !mvIdentity.isConnectToAccess(),
            showLocationDetails: true,
            showReporting: canAccessEverything || mvIdentity.isAnalyst(),
            showScheduled: mvIdentity.canPublish(),
            showSpecialRequest: canAccessEverything || mvIdentity.isSpecialRequester(),
            showTemplates: mvIdentity.canAccessBuilder() || mvIdentity.canAccessPlanner(),
        };
    }, [mvIdentity]);

    const homeHref = React.useMemo(() => {
        if (
            appFrontendRouter &&
            locationId &&
            isDashboardV2Enabled &&
            showGroupsOnDashBoardData?.client?.showGroupsOnDashboard
        ) {
            return appFrontendRouter.get(`/dashboard/brands/?l=${locationId}`);
        } else if (appFrontendRouter && locationId && isDashboardV2Enabled) {
            return appFrontendRouter.get(`/dashboard/?l=${locationId}`);
        } else {
            return '/';
        }
    }, [appFrontendRouter, isDashboardV2Enabled, locationId, showGroupsOnDashBoardData?.client?.showGroupsOnDashboard]);

    // eslint-disable-next-line max-lines-per-function
    const navigation: Navigation<React.ReactNode> = React.useMemo(() => {
        const connectHref =
            appFrontendRouter && isNewConnectPageEnabled
                ? appFrontendRouter.get(`/${AppFrontendRoutes.Connections}?c=${clientId}&l=${locationId}`)
                : '/connect';

        const {
            showAssets,
            showCalendar,
            showConnections,
            showLearn,
            showLocationDetails,
            showReporting,
            showScheduled,
            showSpecialRequest,
            showTemplates,
        } = itemVisibility;

        const brandMenuSectionOneItems: Array<NavigationItem<React.ReactNode>> = [
            {
                icon: <Compass03 />,
                id: 'Overview',
                navigationUrl: `/brand/client/${clientId}/engagement/overview`,
                navigationUrlParts: {
                    endsWith: '/engagement/overview',
                    startsWith: '/brand/client/',
                },
                title: t('MENU.OVERVIEW'),
            },
            {
                icon: <BarChartSquare03 />,
                id: 'Location Activity',
                navigationUrl: `/brand/client/${clientId}/engagement/location-activity`,
                navigationUrlParts: {
                    endsWith: '/engagement/location-activity',
                    startsWith: '/brand/client/',
                },
                title: t('MENU.LOCATION_ACTIVITY'),
            },
            {
                icon: <FileCheck03 />,
                id: 'Template Use',
                navigationUrl: `/brand/client/${clientId}/engagement/template-use`,
                navigationUrlParts: {
                    endsWith: '/engagement/template-use',
                    startsWith: '/brand/client/',
                },
                title: t('MENU.TEMPLATE_USE'),
            },
            {
                icon: <Map02 />,
                id: 'Region/Group Activity',
                navigationUrl: `/brand/client/${clientId}/engagement/region-group-activity`,
                navigationUrlParts: {
                    endsWith: '/engagement/region-group-activity',
                    startsWith: '/brand/client/',
                },
                title: t('MENU.REGION_GROUP_ACTIVITY'),
            },
        ];

        if (canAccessMoreThanOneBrand) {
            brandMenuSectionOneItems.unshift({
                icon: <Passport />,
                id: 'Brands',
                navigationUrl: '/brand/agency/brands',
                navigationUrlParts: { startsWith: '/brand/agency/brands' },
                title: t('MENU.BRANDS'),
            });
        }

        const leftNavigator: Navigation<React.ReactNode> =
            currentView === LeftNavigationView.brand
                ? // Show left nav items for brand view.
                  [
                      {
                          id: 'SECTION 1',
                          isMaintainedOnClose: true,
                          items: brandMenuSectionOneItems,
                      },
                      {
                          hasDividerAbove: true,
                          headerTitle: t('MENU.COMMUNICATE'),
                          id: 'SECTION 2',
                          isMaintainedOnClose: false,
                          items: [
                              {
                                  icon: <Announcement01 />,
                                  id: 'Send',
                                  navigationUrl: '/brand/communicate/send',
                                  navigationUrlParts: {
                                      endsWith: '/send',
                                      startsWith: '/brand/communicate',
                                  },
                                  title: t('MENU.SEND'),
                              },
                              {
                                  icon: <FileCheck02 />,
                                  id: 'Report',
                                  navigationUrl: '/brand/communicate/send-report',
                                  navigationUrlParts: {
                                      endsWith: '/send-report',
                                      startsWith: '/brand/communicate',
                                  },
                                  title: t('MENU.REPORT'),
                              },
                          ],
                      },
                      {
                          hasDividerAbove: true,
                          headerTitle: t('MENU.LOCATIONS'),
                          id: 'SECTION 3',
                          isMaintainedOnClose: false,
                          items: [
                              {
                                  icon: <Ticket01 />,
                                  id: 'Special requests',
                                  navigationUrl: '/specialRequests',
                                  navigationUrlParts: { startsWith: '/specialRequests' },
                                  title: t('MENU.SPECIAL_REQUESTS'),
                              },
                              {
                                  icon: <MarkerPin06 />,
                                  id: 'Locations',
                                  navigationUrl: `/brand/client/${clientId}/manage/locations/locations`,
                                  navigationUrlParts: {
                                      endsWith: '/manage/locations/locations',
                                      startsWith: '/brand/client/',
                                  },
                                  title: t('MENU.LOCATIONS'),
                              },
                              {
                                  icon: <Globe04 />,
                                  id: 'Regions/Groups',
                                  navigationUrl: `/brand/client/${clientId}/manage/regions-groups/regions-groups`,
                                  navigationUrlParts: {
                                      endsWith: '/manage/regions-groups/regions-groups',
                                      startsWith: '/brand/client/',
                                  },
                                  title: t('MENU.REGIONS_GROUPS'),
                              },
                              {
                                  icon: <Users01 />,
                                  id: 'Location Users',
                                  navigationUrl: `/brand/client/${clientId}/manage/users/users`,
                                  navigationUrlParts: {
                                      endsWith: '/manage/users/users',
                                      startsWith: '/brand/client/',
                                  },
                                  title: t('MENU.LOCATION_USERS'),
                              },
                              {
                                  icon: <Link04 />,
                                  id: 'Connect',
                                  navigationUrl: connectHref,
                                  navigationUrlParts: {
                                      startsWith: '/connect',
                                  },
                                  title: t('MENU.CONNECTIONS'),
                              },
                          ],
                      },
                      {
                          hasDividerAbove: true,
                          headerTitle: t('MENU.BRAND_SETTINGS'),
                          id: 'SECTION 5',
                          isMaintainedOnClose: false,
                          items: [
                              {
                                  icon: <Globe04 />,
                                  id: 'Clients',
                                  navigationUrl: '/agency/clients',
                                  navigationUrlParts: {
                                      startsWith: '/agency/clients',
                                  },
                                  title: t('MENU.CLIENTS'),
                              },
                              {
                                  icon: <GraduationHat02 />,
                                  id: 'Learn Menu',
                                  navigationUrl: `/brand/client/${clientId}/manage/learn-menu`,
                                  navigationUrlParts: {
                                      endsWith: '/manage/learn-menu',
                                      startsWith: '/brand/client/',
                                  },
                                  title: t('MENU.LEARN_MENU'),
                              },
                              {
                                  icon: <Beaker02 />,
                                  id: 'Calendar Activities',
                                  navigationUrl: `/brand/client/${clientId}/manage/activities`,
                                  navigationUrlParts: {
                                      endsWith: '/manage/activities',
                                      startsWith: '/brand/client/',
                                  },
                                  title: t('MENU.CALENDAR_ACTIVITIES'),
                              },
                              {
                                  icon: <Bookmark />,
                                  id: 'Template Categories',
                                  navigationUrl: `/brand/client/${clientId}/manage/template-categories`,
                                  navigationUrlParts: {
                                      endsWith: '/manage/template-categories',
                                      startsWith: '/brand/client/',
                                  },
                                  title: t('MENU.TEMPLATE_CATEGORIES'),
                              },
                              {
                                  icon: <Hash01 />,
                                  id: 'Hashtag Sets',
                                  navigationUrl: `/brand/client/${clientId}/manage/hashtags`,
                                  navigationUrlParts: {
                                      endsWith: '/manage/hashtags',
                                      startsWith: '/brand/client/',
                                  },
                                  title: t('MENU.HASHTAG_SETS'),
                              },
                              {
                                  icon: <Brush03 />,
                                  id: 'Styles',
                                  navigationUrl: `/brand/client/${clientId}/style`,
                                  navigationUrlParts: {
                                      endsWith: '/style',
                                      startsWith: '/brand/client/',
                                  },
                                  subItems: [
                                      {
                                          id: 'Fonts',
                                          navigationUrl: `/brand/client/${clientId}/style/fonts`,
                                          navigationUrlParts: {
                                              endsWith: '/style/fonts',
                                              startsWith: '/brand/client/',
                                          },
                                          title: t('MENU.FONTS'),
                                      },
                                      {
                                          id: 'Colors',
                                          navigationUrl: `/brand/client/${clientId}/style/colors`,
                                          navigationUrlParts: {
                                              endsWith: '/style/colors',
                                              startsWith: '/brand/client/',
                                          },
                                          title: t('MENU.COLORS'),
                                      },
                                      {
                                          id: 'Style Guide',
                                          navigationUrl: `/brand/client/${clientId}/style/style-guide`,
                                          navigationUrlParts: {
                                              endsWith: '/style/style-guide',
                                              startsWith: '/brand/client/',
                                          },
                                          title: t('MENU.STYLE_GUIDE'),
                                      },
                                  ],
                                  title: t('MENU.STYLES'),
                              },
                          ],
                      },
                      {
                          hasDividerAbove: true,
                          headerTitle: t('MENU.ROADMAP'),
                          id: 'SECTION 6',
                          isMaintainedOnClose: false,
                          items: [
                              {
                                  icon: <Route />,
                                  id: 'Roadmap',
                                  navigationUrl: '/brand/roadmap',
                                  navigationUrlParts: {
                                      endsWith: '/roadmap',
                                      startsWith: '/brand/roadmap',
                                  },
                                  title: t('MENU.ROADMAP'),
                              },
                          ],
                      },
                  ]
                : // Show left nav items for location view.
                  [
                      {
                          id: 'SECTION 1',
                          isMaintainedOnClose: true,
                          items: [
                              {
                                  icon: <GraduationHat02 />,
                                  id: 'Learn',
                                  isDisabled: !showLearn,
                                  navigationUrl: '/trythis',
                                  navigationUrlParts: {
                                      startsWith: '/trythis',
                                  },
                                  title: t('MENU.LEARN'),
                              },
                              {
                                  icon: <CalendarDate />,
                                  id: 'Plan',
                                  isDisabled: !showCalendar,
                                  navigationUrl: '/planner',
                                  navigationUrlParts: {
                                      startsWith: '/planner',
                                  },
                                  title: t('MENU.CALENDAR'),
                              },
                              {
                                  icon: <Send01 />,
                                  id: 'Scheduled',
                                  isDisabled: !showScheduled,
                                  navigationUrl:
                                      appFrontendRouter && isNewScheduledPageEnabled
                                          ? appFrontendRouter.get(`/scheduled/?c=${clientId}&l=${locationId}`)
                                          : '/scheduled',
                                  navigationUrlParts: {
                                      startsWith: '/scheduled',
                                  },
                                  title: t('MENU.SCHEDULED'),
                              },
                              {
                                  icon: <FileShield03 />,
                                  id: 'Templates',
                                  isDisabled: !showTemplates,
                                  navigationUrl:
                                      appFrontendRouter && isNewGalleryPageEnabled && clientId && locationId
                                          ? appFrontendRouter.get(`/templates/?c=${clientId}&l=${locationId}`)
                                          : '/builderTemplateGallery',
                                  navigationUrlParts: {
                                      startsWith: '/builderTemplateGallery',
                                  },
                                  title: t('MENU.TEMPLATES'),
                              },
                              {
                                  icon: <Image03 />,
                                  id: 'Assets',
                                  isDisabled: !showAssets,
                                  navigationUrl:
                                      appFrontendRouter && isNewAssetLibraryEnabled
                                          ? appFrontendRouter.get(`/assets/?c=${clientId}&l=${locationId}`)
                                          : '/assets/view',
                                  navigationUrlParts: {
                                      startsWith: '/assets/view',
                                  },
                                  title: t('MENU.ASSETS'),
                              },
                              {
                                  icon: <PieChart03 />,
                                  id: 'Reporting',
                                  isDisabled: !showReporting,
                                  navigationUrl: `/reports/location/${locationId}/overview`,
                                  navigationUrlParts: {
                                      endsWith: '/overview',
                                      startsWith: '/reports/location/',
                                  },
                                  title: t('MENU.REPORTING'),
                              },
                              {
                                  icon: <Ticket01 />,
                                  id: 'Special requests',
                                  isDisabled: !showSpecialRequest,
                                  navigationUrl: '/specialRequests',
                                  navigationUrlParts: {
                                      startsWith: '/specialRequests',
                                  },
                                  title: t('MENU.REQUESTS'),
                              },
                              {
                                  icon: <Link04 />,
                                  id: 'Connect',
                                  isDisabled: !showConnections,
                                  navigationUrl: connectHref,
                                  navigationUrlParts: {
                                      startsWith: '/connect',
                                  },
                                  title: t('MENU.CONNECTIONS'),
                              },
                              {
                                  icon: <MarkerPin01 />,
                                  id: 'Location Details',
                                  isDisabled: !showLocationDetails,
                                  navigationUrl:
                                      appFrontendRouter && isLocationDetailsV2Enabled
                                          ? appFrontendRouter.get(`/location/details?l=${locationId}`)
                                          : '/locationDetails',
                                  navigationUrlParts: {
                                      startsWith: '/locationDetails',
                                  },
                                  title: t('MENU.LOCATION_DETAILS'),
                              },
                          ],
                      },
                  ];

        const leftNavigatorWithCurrentSelection = leftNavigator.map(section => ({
            ...section,
            items: section.items
                .map(item => {
                    const subItems = item.subItems
                        ?.map(subItem => ({
                            ...subItem,
                            isSelected:
                                subItem.navigationUrlParts &&
                                doesUrlMatchNavigationParts(currentUrlPath, subItem.navigationUrlParts),
                        }))
                        .filter(subItem => !subItem.isDisabled);

                    return {
                        ...item,
                        isSelected:
                            (item.navigationUrlParts &&
                                doesUrlMatchNavigationParts(currentUrlPath, item.navigationUrlParts)) ||
                            subItems?.some(subItem => subItem.isSelected),
                        subItems,
                    };
                })
                .filter(item => !item.isDisabled),
        }));

        return leftNavigatorWithCurrentSelection;
    }, [
        appFrontendRouter,
        canAccessMoreThanOneBrand,
        clientId,
        currentUrlPath,
        currentView,
        isLocationDetailsV2Enabled,
        isNewAssetLibraryEnabled,
        isNewConnectPageEnabled,
        isNewGalleryPageEnabled,
        isNewScheduledPageEnabled,
        itemVisibility,
        locationId,
    ]);

    return { homeHref, isLoading: loading || userBrandCountLoading, navigation };
}
